import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { verify2FA } from '../../../redux/Slices/authSlice';
import { setUserRoles } from '../../../redux/Slices/PermissionsSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { useTitle } from '../../../shared/context/TitleContext';
import { getWebFcmToken } from '../../../firebase/webPush';

const AdminVerify2FA = () => {
    const [searchParams] = useSearchParams();
    const emailParam = searchParams.get('email') || '';
    const email = emailParam || (() => {
        try {
            return sessionStorage.getItem('adminEmail') || '';
        } catch { return ''; }
    })();
    const [verificationCode, setVerificationCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [shake, setShake] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { setTitle } = useTitle();

    useEffect(() => {
        setTitle('التحقق من المصادقة الثنائية');
        if (!email) {
            showErrorToast('البريد الإلكتروني مطلوب');
            navigate('/admin-login');
        }
    }, [email, navigate, setTitle]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!verificationCode.trim() || verificationCode.length !== 6) {
            setShake(true);
            showErrorToast('يرجى إدخال كود التحقق المكون من 6 أرقام');
            setTimeout(() => setShake(false), 500);
            return;
        }

        try {
            setLoading(true);
            let webFcmToken = '';
            try {
                webFcmToken = await getWebFcmToken();
            } catch {
                webFcmToken = '';
            }
            const result = await dispatch(
                verify2FA({
                    email,
                    code: verificationCode.trim(),
                    web_fcm_token: webFcmToken || '',
                })
            ).unwrap();
            
            // البيانات محفوظة بالفعل في Redux reducer (sessionStorage)
            
            // Save roles and permissions - التحقق من البنية الجديدة
            if (result.data?.admin && Array.isArray(result.data.admin) && result.data.admin.length > 0) {
                const roles = result.data.admin.map(admin => ({
                    role_name_en: admin.role_name_en,
                    permissions: admin.permissions || []
                }));
                dispatch(setUserRoles({ roles }));
            } else if (result.data?.roles) {
                dispatch(setUserRoles({ roles: result.data.roles }));
            }
            
            showSuccessToast('تم التحقق بنجاح');
            setTimeout(() => {
                window.location.href = '/';
            }, 1500);
        } catch (err) {
            setShake(true);
            showErrorToast(err.message || 'كود التحقق غير صحيح');
            setTimeout(() => setShake(false), 500);
        } finally {
            setLoading(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">جاري التحقق...</span>
        </div>
    );

    return (
        <div className="container-fluid d-flex p-0 auth-page"
            style={{
                minHeight: '100vh',
                background: 'white'
            }}>
            
            {/* النموذج في المنتصف */}
            <div className="col-12 d-flex justify-content-center align-items-center px-4 my-5">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className={`card shadow-lg ${shake ? 'shake-animation' : ''}`}
                    style={{
                        width: '100%',
                        maxWidth: '450px',
                        border: 'none',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}>

                    <div className="card-body p-5">
                        {/* اللوجو */}
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="text-center mb-4">
                            <img 
                                src="/logokhadamat.png" 
                                alt="Khadamat Logo" 
                                style={{
                                    height: '80px',
                                    width: 'auto'
                                }}
                            />
                        </motion.div>

                        {/* عنوان */}
                        <motion.h3
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-center mb-3 fw-bold"
                            style={{ color: '#333', fontSize: '18px' }}>
                            <FaCheckCircle className="mx-2 text-success" />
                            التحقق من المصادقة الثنائية
                        </motion.h3>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="text-center mb-4"
                            style={{ 
                                color: '#184194',
                                fontSize: '1rem',
                                fontWeight: '500'
                            }}>
                            أدخل كود التحقق من تطبيق Google Authenticator
                        </motion.p>

                        <form onSubmit={handleSubmit}>
                            {/* حقل كود التحقق */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.7 }}
                                className="mb-4">
                                <label htmlFor="code" className="form-label fw-medium" style={{ color: '#333' }}>
                                    كود التحقق (6 أرقام)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="code"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="أدخل الكود من Google Authenticator"
                                    maxLength="6"
                                    required
                                    autoFocus
                                    style={{ 
                                        fontSize: '24px',
                                        textAlign: 'center',
                                        letterSpacing: '8px',
                                        fontFamily: 'monospace',
                                        fontWeight: 'bold'
                                    }}
                                />
                                <small className="text-muted d-block mt-2 text-center">
                                    أدخل الكود المكون من 6 أرقام من تطبيق Google Authenticator
                                </small>
                            </motion.div>

                            {/* زر التحقق */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="btn w-100 text-white py-3 fw-medium mb-3"
                                style={{
                                    backgroundColor: '#184194',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '1.1rem',
                                    marginTop: '1rem'
                                }}
                                disabled={loading || verificationCode.length !== 6}>
                                {loading ? <LoadingSpinner /> : 'تحقق وتسجيل الدخول'}
                            </motion.button>

                            {/* زر الرجوع */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={() => navigate('/admin-login')}
                                style={{ borderRadius: '10px' }}>
                                <FaArrowLeft className="mx-2" />
                                رجوع
                            </motion.button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default AdminVerify2FA;

